import * as React from "react";
import StarIcon from "@mui/icons-material/Star";
import {
  Box,
  Chip,
  FormHelperText,
  Link,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  List,
  ListItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Talent } from "../../types/users";
import TalentDrawer from "../talent/TalentDrawer";
import dayjs from "dayjs";
import {
  NegotiationStatus,
  Project,
  ProjectResource,
  ProjectStatus,
  ReportProgress,
  ReportProgressStatus,
} from "../../types/project";
import { getLastProgress } from "../../utils/projectHelper";
import { ucFirstAllWords } from "../../utils/helper";
import { LoadingButton } from "@mui/lab";
import { Refresh } from "@mui/icons-material";
import { useGetProjectBidsQuery } from "../../services/projectJob.service";
import { CardBase, GlassTable, Button, BaseModal } from "../../components/global";

const getBidStatus = (
  status: NegotiationStatus
): {
  label: string;
  color: any;
} => {
  if (status == NegotiationStatus.NEW) {
    return {
      label: "NEW",
      color: undefined,
    };
  } else if (status == NegotiationStatus.HIRED) {
    return {
      label: "HIRED",
      color: "success",
    };
  } else if (status == NegotiationStatus.INTERVIEW) {
    return {
      label: "INTERVIEWED",
      color: "warning",
    };
  } else if (status == NegotiationStatus.INVITED) {
    return {
      label: "INVITED",
      color: "info",
    };
  } else if (status == NegotiationStatus.REJECTED) {
    return {
      label: "REJECTED",
      color: "error",
    };
  } else if (status == NegotiationStatus.CANCELED) {
    return {
      label: "CANCELED",
      color: "error",
    };
  } else {
    return { label: status, color: undefined };
  }
};

const getHiredStatus = (
  project: Project,
  job: ProjectResource
): {
  label: string;
  color: any;
  helperText?: string;
} => {
  const progress = getLastProgress(job);
  if (project.status == ProjectStatus.PUBLISHED) {
    return {
      label: "WAIT for Independepent Professional",
      color: undefined,
      helperText: "Waiting for more Independent Professionals",
    };
  } else if (progress?.status == ReportProgressStatus.APPROVED) {
    return { label: "WORK APPROVED", color: "success" };
  } else if (progress?.status == ReportProgressStatus.REJECTED) {
    return { label: "WORK REJECTED", color: "error" };
  } else if (progress?.status == ReportProgressStatus.SUBMITTED) {
    return {
      label: "WAIT APPROVAL",
      color: "info",
    };
  } else if (!!progress?.check_in && !progress?.check_out) {
    return {
      label: "CHECKED IN",
      color: "info",
      helperText: `at ${dayjs(progress?.check_in).format("MMM DD hh:mm A")}`,
    };
  } else if (!!progress?.check_in && !!progress?.check_out) {
    return {
      label: "CHECKED OUT",
      color: "warning",
      helperText: `at ${dayjs(progress?.check_out).format("MMM DD hh:mm A")}`,
    };
  }
  return { label: job?.status?.toUpperCase(), color: undefined };
};

const LogModal = ({
  open,
  onClose,
  job,
}: {
  open: boolean;
  onClose: () => void;
  job: ProjectResource | null;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const sorted = React.useMemo(
    () =>
      job?.report_progresses
        ?.slice()
        .sort(
          (a: ReportProgress, b: ReportProgress) =>
            new Date(a.check_in).getTime() - new Date(b.check_in).getTime()
        ),
    [job]
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Log"
      maxWidth="md"
      useGlassCard={false}
      footer={
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      }
    >
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <List sx={{ pl: 0 }}>
          <ListItem sx={{ pl: 0, display: "list-item", listStyleType: "disc" }}>
            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
              Hired at {dayjs(job?.hired_at).format("MMM DD, YYYY hh:mm A")}
            </Typography>
          </ListItem>
          {sorted?.map((it, idx) => (
            <React.Fragment key={idx}>
              <ListItem sx={{ pl: 0, display: "list-item", listStyleType: "disc" }}>
                <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                  Working from {dayjs(it.check_in).format("MMM DD, YYYY hh:mm A")} {" --> "}
                  {!!it.check_out
                    ? dayjs(it.check_out).format("MMM DD, YYYY hh:mm A")
                    : "-"}
                </Typography>
              </ListItem>
              {!!it.status && (
                <>
                  <ListItem sx={{ pl: 0, display: "list-item", listStyleType: "disc" }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>Work Submitted</Typography>
                  </ListItem>
                  <ListItem sx={{ pl: 0, display: "list-item", listStyleType: "disc" }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>{ucFirstAllWords(it.status)}</Typography>
                  </ListItem>
                </>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </BaseModal>
  );
};

const TableHireds = ({
  project,
  bids,
}: {
  project: Project;
  bids: Array<ProjectResource>;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedTalent, setSelectedTalent] = React.useState<Talent | null>(
    null
  );
  const [logModal, setLogModal] = React.useState<{
    visible: boolean;
    job: ProjectResource | null;
  }>({
    visible: false,
    job: null,
  });

  return (
    <CardBase
      title="Hired Talents"
      useGlassCard={false}
      sx={{ mb: { xs: 2, sm: 3 } }}
    >
      <GlassTable>
        <Table sx={{ minWidth: { xs: "100%", sm: 650 } }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: "#F4A259", fontSize: { xs: "0.75rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>Talent</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#F4A259", fontSize: { xs: "0.75rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>Hired At</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#F4A259", fontSize: { xs: "0.75rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>Rate ($/hr)</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#F4A259", fontSize: { xs: "0.75rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>Max Hour</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#F4A259", fontSize: { xs: "0.75rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#F4A259", fontSize: { xs: "0.75rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bids?.length == 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>No talents hired, yet</TableCell>
              </TableRow>
            )}
            {bids?.map((job) => {
              const status = getHiredStatus(project, job);
              const progress = getLastProgress(job);
              const reviewForTalent = project?.reviews?.find(
                (it) =>
                  it.model_type == "user" && it.model_id == job.resource_id
              );
              const reviewForClient = project?.reviews?.find(
                (it) =>
                  it.model_type == "company" && it.created_by == job.resource_id
              );
              return (
                <>
                  <TableRow
                    key={job.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell sx={{ padding: { xs: "8px 4px", sm: "16px" } }}>
                      <Stack direction={"row"} alignItems="center">
                        <Box>
                          <Stack direction={"row"} alignItems="center" flexWrap="wrap">
                            <Link
                              onClick={() => {
                                setSelectedTalent(job.resource || null);
                                setDrawerOpen(true);
                              }}
                              sx={{
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                color: "#F4A259",
                                "&:hover": {
                                  color: "#E8944A",
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              {job.resource?.first_name}{" "}
                              {job.resource?.last_name}
                            </Link>
                            <Rating
                              value={job.resource?.total_rating}
                              readOnly
                              precision={0.5}
                              size={isMobile ? "small" : "small"}
                              emptyIcon={
                                <StarIcon
                                  style={{ opacity: 0.55 }}
                                  fontSize="inherit"
                                />
                              }
                              sx={{ ml: 1 }}
                            />
                          </Stack>
                          <Typography variant="caption" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                            {job.resource?.city}, {job.resource?.state}{" "}
                            {job.resource?.zip}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>
                      {dayjs(job.hired_at).format("MMM DD, YYYY")}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>{job.rate}</TableCell>
                    <TableCell sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>{job.max_hour} hr</TableCell>
                    <TableCell sx={{ padding: { xs: "8px 4px", sm: "16px" } }}>
                      <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                        sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                      />
                      {!!status.helperText && (
                        <FormHelperText sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>{status.helperText}</FormHelperText>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ padding: { xs: "8px 4px", sm: "16px" } }}>
                      <Button
                        color="info"
                        variant="text"
                        onClick={() => setLogModal({ visible: true, job })}
                        size={isMobile ? "small" : "medium"}
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        Log
                      </Button>
                    </TableCell>
                  </TableRow>
                  {project.status == ProjectStatus.COMPLETED && (
                    <TableRow>
                      <TableCell />
                      <TableCell colSpan={2} sx={{ alignItems: "flex-start", padding: { xs: "8px 4px", sm: "16px" } }}>
                        <Typography variant="subtitle2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                          Review for Talent:
                        </Typography>
                        {!!reviewForTalent ? (
                          <Box>
                            <Rating
                              value={reviewForTalent.star}
                              readOnly={true}
                              size={isMobile ? "small" : "medium"}
                            />
                            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                              {reviewForTalent.review}
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="body2" sx={{ color: "#999", fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                              No review, yet
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell colSpan={3} sx={{ verticalAlign: "top", padding: { xs: "8px 4px", sm: "16px" } }}>
                        <Typography variant="subtitle2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                          Review for Customer:
                        </Typography>
                        {!!reviewForClient ? (
                          <Box>
                            <Rating
                              value={reviewForClient.star}
                              readOnly={true}
                              size={isMobile ? "small" : "medium"}
                            />
                            <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                              {reviewForClient.review}
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="body2" sx={{ color: "#999", fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                              No review, yet
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
          </TableBody>
        </Table>
      </GlassTable>
      <LogModal
        open={logModal.visible}
        job={logModal.job || null}
        onClose={() => setLogModal({ visible: false, job: null })}
      />
      <TalentDrawer
        forceRefresh={true}
        data={selectedTalent}
        open={drawerOpen}
        setOpen={setDrawerOpen}
      />
    </CardBase>
  );
};

const TableBids = ({
  project,
  bids,
}: {
  project: Project;
  bids: Array<ProjectResource>;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedTalent, setSelectedTalent] = React.useState<Talent | null>(
    null
  );
  const [hireModal, setHireModal] = React.useState<{
    visible: boolean;
    job: ProjectResource | null;
  }>({
    visible: false,
    job: null,
  });
  const [showTable, setShowTable] = React.useState(
    project.status == ProjectStatus.PUBLISHED
  );
  const isHourly = React.useMemo(() => project?.type == "hourly", [project]);

  const isAcceptingBid = React.useMemo(
    () => project.status == ProjectStatus.PUBLISHED,
    [project]
  );

  return (
    <CardBase
      title="Incoming Bids"
      useGlassCard={false}
      headerActions={
        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowTable(!showTable)}
          sx={{
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            borderColor: "#F4A259",
            color: "#F4A259",
            "&:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.08)",
              borderColor: "#F4A259",
            },
          }}
        >
          {showTable ? "Hide" : "Show"}
        </Button>
      }
      sx={{ mb: { xs: 2, sm: 3 } }}
    >
      {showTable && (
        <GlassTable>
          <Table sx={{ minWidth: { xs: "100%", sm: 650 } }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: "#F4A259", fontSize: { xs: "0.75rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>Talent</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#F4A259", fontSize: { xs: "0.75rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>Price</TableCell>
                {isHourly && <TableCell sx={{ fontWeight: 600, color: "#F4A259", fontSize: { xs: "0.75rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>Max Hour</TableCell>}
                <TableCell sx={{ fontWeight: 600, color: "#F4A259", fontSize: { xs: "0.75rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#F4A259", fontSize: { xs: "0.75rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>Updated</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#F4A259", fontSize: { xs: "0.75rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bids?.length == 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>No bids, yet</TableCell>
                </TableRow>
              )}
              {bids?.map((job) => {
                const status = getBidStatus(job.status);
                return (
                  <TableRow
                    key={job.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell sx={{ padding: { xs: "8px 4px", sm: "16px" } }}>
                      <Stack direction={"row"} alignItems="center">
                        <Box>
                          <Stack direction={"row"} alignItems="center" flexWrap="wrap">
                            <Link
                              onClick={() => {
                                setSelectedTalent(job.resource || null);
                                setDrawerOpen(true);
                              }}
                              sx={{
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                color: "#F4A259",
                                "&:hover": {
                                  color: "#E8944A",
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              {job.resource?.first_name}{" "}
                              {job.resource?.last_name}
                            </Link>
                            <Rating
                              value={job.resource?.total_rating}
                              readOnly
                              precision={0.5}
                              size={isMobile ? "small" : "small"}
                              emptyIcon={
                                <StarIcon
                                  style={{ opacity: 0.55 }}
                                  fontSize="inherit"
                                />
                              }
                              sx={{ ml: 1 }}
                            />
                          </Stack>
                          <Typography variant="caption" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                            {job.resource?.city}, {job.resource?.state}{" "}
                            {job.resource?.zip}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>
                      {job.status == NegotiationStatus.INVITED
                        ? "n/a"
                        : isHourly
                          ? `$${job.rate}/hr`
                          : `$${job.price}`}
                    </TableCell>
                    {isHourly && <TableCell sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>{job.max_hour} hr</TableCell>}
                    <TableCell sx={{ padding: { xs: "8px 4px", sm: "16px" } }}>
                      <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                        sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                      />
                      {status.label == "CANCELED" && (
                        <Typography variant="caption" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                          <br /> {job.canceled_reason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" }, padding: { xs: "8px 4px", sm: "16px" } }}>
                      {dayjs(job.updated_at).format("MMM DD, YYYY hh:mm A")}
                    </TableCell>
                    <TableCell align="right" sx={{ padding: { xs: "8px 4px", sm: "16px" } }}>
                      {/* {isAcceptingBid && (
                        <>
                          <Button
                            variant="contained"
                            onClick={() => setHireModal({ visible: true, job })}
                          >
                            Hire
                          </Button>
                          <Button variant="outlined" sx={{ ml: 1 }}>
                            Negotiate
                          </Button>
                        </>
                      )} */}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </GlassTable>
      )}
      {!showTable && (
        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 2, fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
          Table is hidden
        </Typography>
      )}
      <TalentDrawer
        forceRefresh={true}
        data={selectedTalent}
        open={drawerOpen}
        setOpen={setDrawerOpen}
      />
    </CardBase>
  );
};

const ProjectTalentsPanel = ({ project }: { project: Project; }) => {
  // const navigate = useNavigate();
  const {
    data: jobs,
    isLoading,
    isFetching,
    refetch,
  } = useGetProjectBidsQuery(project.id);

  const handleRefresh = () => {
    refetch();
  };

  const hiredList = React.useMemo(
    () => jobs?.filter((it) => !!it.hired_at),
    [jobs]
  );
  const bidList = React.useMemo(
    () => jobs?.filter((it) => !it.hired_at),
    [jobs]
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: { xs: 1.5, sm: 2 } }}>
        <LoadingButton
          variant="outlined"
          onClick={handleRefresh}
          size={isMobile ? "small" : "small"}
          loading={isFetching}
          startIcon={<Refresh />}
          sx={{
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            borderColor: "#F4A259",
            color: "#F4A259",
            "&:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.08)",
              borderColor: "#F4A259",
            },
            "&:disabled": {
              borderColor: "rgba(244, 162, 89, 0.5)",
              color: "rgba(244, 162, 89, 0.5)",
            },
          }}
        >
          Refresh
        </LoadingButton>
      </Stack>
      <TableHireds bids={hiredList || []} project={project} />
      <Box sx={{ mt: { xs: 2, sm: 3 } }} />
      <TableBids bids={bidList || []} project={project} />
    </Box>
  );
};
export default ProjectTalentsPanel;
